import './WidgetStyle.css'; 
import './ResponsiveFonts.css';
import { useFont } from '../../Design/FontContext';
import React, { useState, useEffect, useRef } from 'react';
import DeleteModal from './DeleteModal';
import { getTextEffectStyles } from '../utils/textEffectsUtils';
import '../WidgetEditComponents/TextEffectsAnimations.css';
import { generateLayoutStyles, getResponsiveValue, appendPx } from '../WidgetEditComponents/LayoutUtils';
import HighlightText from '../WidgetEditComponents/HighlightText';
import { renderSafeRichText } from '../utils/richTextUtils';
import AIRefreshIcon from '../../components/AIRefreshIcon';

function GalleryWidget({ data, onRemove, isSelected, isEditMode, onUpdate, onSelectWidget }) {
    const deleteLogo = '/img/delete.svg';
    const fonts = useFont();
    
    // ✅ INLINE-EDITING SYSTEM - EXAKT WIE HEROHEADER
    const [isInlineEditing, setIsInlineEditing] = useState(null);
    const [inlineValue, setInlineValue] = useState('');
    const [originalLayout, setOriginalLayout] = useState({});
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    
    // Lightbox State
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxImage, setLightboxImage] = useState(null);
    
    // Animation Trigger State (2025 Animation System)
    const [animationKey, setAnimationKey] = useState(0);
    
    // Mobile Layout State - MUSS FRÜH definiert werden
    const [isMobileLayout, setIsMobileLayout] = useState(false);
    const containerRef = useRef(null);
    
    // Ref für Outside-Click Detection
    const widgetRef = useRef(null);

    // Animation Trigger Effect (2025 Animation System)
    useEffect(() => {
        if (data.triggerAnimation && isEditMode) {
            setAnimationKey(prev => prev + 1);
        }
    }, [data.triggerAnimation, isEditMode]);

    // Delete Handler
    const handleDelete = (event) => {
        event.stopPropagation();
        if (onRemove) {
            onRemove({ stopPropagation: () => {} }, data.id);
        }
        setShowDeleteModal(false);
    };

    // ✅ SIMPLIFIED: Container-basierte Breakpoint-Erkennung (Standard wie alle Widgets)
    useEffect(() => {
        const checkContainerWidth = () => {
            if (containerRef.current) {
                const containerWidth = containerRef.current.offsetWidth;
                setIsMobileLayout(containerWidth < 750); // Standard 750px Breakpoint
            }
        };

        checkContainerWidth();
        window.addEventListener('resize', checkContainerWidth);
        
        const observer = new ResizeObserver(checkContainerWidth);
        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => {
            window.removeEventListener('resize', checkContainerWidth);
            observer.disconnect();
        };
    }, []);

    // ✅ INLINE-EDITING FUNCTIONS - EXAKT WIE HEROHEADER
    const handleInlineEdit = (fieldName, currentValue) => {
        if (!onUpdate) return;
        
        // Widget selektieren wenn nicht bereits selektiert
        if (!isSelected && onSelectWidget) {
            onSelectWidget(data.id);
        }
        
        // Toggle functionality: If already editing this field, cancel editing
        if (isInlineEditing === fieldName) {
            handleInlineCancel();
            return;
        }
        
        setIsInlineEditing(fieldName);
        setInlineValue(currentValue || '');
    };

    const handleInlineSubmit = (fieldName) => {
        if (!onUpdate || !fieldName || fieldName !== isInlineEditing) return;
        
        // Update the widget data
        const updatedData = { ...data, [fieldName]: inlineValue };
        onUpdate(updatedData);
        
        // Reset inline editing state
        setIsInlineEditing(null);
        setInlineValue('');
    };

    const handleInlineCancel = () => {
        setIsInlineEditing(null);
        setInlineValue('');
    };

    const handleInlineKeyPress = (e, fieldName) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleInlineSubmit(fieldName);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            handleInlineCancel();
        }
    };

    // Smart Outside-Click Handler
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!isInlineEditing) return;
            
            const isEditingInput = event.target.tagName === 'TEXTAREA' || event.target.tagName === 'INPUT';
            const isActionButton = event.target.hasAttribute('data-action-button');
            const isDeleteButton = event.target.closest('.delete_button');
            const isWidgetEdit = event.target.closest('.widget-edit');
            const isAddWidgets = event.target.closest('.add-widgets');
            const isPagePreview = event.target.closest('.page_preview');
            
            const isRealOutsideClick = !isEditingInput && 
                                     !isActionButton && 
                                     !isDeleteButton && 
                                     !isWidgetEdit &&
                                     (isPagePreview || !widgetRef.current?.contains(event.target));
            
            if (isRealOutsideClick) {
                if (onUpdate && inlineValue !== undefined) {
                    const updatedData = { ...data, [isInlineEditing]: inlineValue };
                    onUpdate(updatedData);
                }
                setIsInlineEditing(null);
                setInlineValue('');
            }
        };

        if (isInlineEditing) {
            document.addEventListener('mousedown', handleClickOutside, true);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside, true);
            };
        }
    }, [isInlineEditing, inlineValue, data, onUpdate]);

    // ✅ RENDER EDITABLE TEXT FUNCTION - AKTUALISIERT MIT NEUEN STANDARDS
    const renderEditableText = (fieldName, currentValue, Element = 'p', style = {}, placeholder = 'Click to edit...') => {
        if (!isEditMode) {
            return currentValue ? (
                <Element style={{
                    ...style,
                    whiteSpace: style.whiteSpace || 'pre-wrap'
                }}>
                    {currentValue}
                </Element>
            ) : null;
        }

        if (isInlineEditing === fieldName) {
            const layoutData = originalLayout;
            const isTextarea = Element !== 'button';
            
            // Action Buttons Container
            const renderActionButtons = () => (
                <div style={{
                    position: 'absolute',
                    top: Element === 'button' ? '-11px' : '-11px',
                    right: Element === 'button' ? '-6px' : '-6px',
                    display: 'flex',
                    gap: '4px',
                    zIndex: 9999,
                    background: 'rgba(255, 255, 255, 0.95)',
                    padding: '2px 4px',
                    borderRadius: '6px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    backdropFilter: 'blur(4px)',
                    border: '1px solid rgba(0, 0, 0, 0.05)'
                }}>
                    {/* Grüner Haken - Speichern */}
                    <button
                        data-action-button="save"
                        onClick={() => handleInlineSubmit(fieldName)}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{
                            background: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            padding: '0',
                            fontSize: '9px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '16px',
                            height: '16px',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 1px 3px rgba(16, 185, 129, 0.3)'
                        }}
                        title="Save"
                    >
                        ✓
                    </button>
                    
                    {/* Rotes X - Abbrechen */}
                    <button
                        data-action-button="cancel"
                        onClick={handleInlineCancel}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            padding: '0',
                            fontSize: '9px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '16px',
                            height: '16px',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 1px 3px rgba(239, 68, 68, 0.3)'
                        }}
                        title="Cancel"
                    >
                        ×
                    </button>
                </div>
            );
            
            return isTextarea ? (
                <div style={{ 
                    position: 'relative',
                    width: '100%',
                    marginTop: layoutData.marginTop || '0',
                    marginBottom: layoutData.marginBottom || '0',
                    marginLeft: layoutData.marginLeft || '0',
                    marginRight: layoutData.marginRight || '0'
                }}>
                    <textarea
                        value={inlineValue}
                        onChange={(e) => {
                            setInlineValue(e.target.value);
                            const target = e.target;
                            const cursorPosition = target.selectionStart;
                            setTimeout(() => {
                                target.style.height = 'auto';
                                target.style.height = target.scrollHeight + 'px';
                                target.setSelectionRange(cursorPosition, cursorPosition);
                            }, 0);
                        }}
                        onKeyDown={(e) => {
                            handleInlineKeyPress(e, fieldName);
                            if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey) {
                                setTimeout(() => {
                                    const target = e.target;
                                    target.style.height = 'auto';
                                    target.style.height = target.scrollHeight + 'px';
                                }, 0);
                            }
                        }}
                        onBlur={() => handleInlineSubmit(fieldName)}
                        placeholder={placeholder}
                        autoFocus
                        onInput={(e) => {
                            e.target.style.height = 'auto';
                            const originalHeight = parseInt(layoutData.height) || parseInt(style.fontSize) * 1.6;
                            e.target.style.height = Math.max(e.target.scrollHeight, originalHeight) + 'px';
                        }}
                        ref={(textarea) => {
                            if (textarea) {
                                setTimeout(() => {
                                    const originalHeight = parseInt(layoutData.height) || parseInt(style.fontSize) * 1.6;
                                    textarea.style.height = 'auto';
                                    textarea.style.height = Math.max(textarea.scrollHeight, originalHeight) + 'px';
                                    if (textarea.selectionStart === 0 && textarea.selectionEnd === 0) {
                                        textarea.setSelectionRange(textarea.value.length, textarea.value.length);
                                    }
                                }, 0);
                            }
                        }}
                        style={{
                            ...style,
                            background: 'transparent',
                            border: 'none',
                            outline: 'none',
                            resize: 'none',
                            padding: '4px 8px',
                            margin: '0',
                            width: '100%',
                            minHeight: layoutData.height || 'auto',
                            // GRADIENT-FIX: Grauer Text während Bearbeitung
                            color: '#333333',
                            backgroundImage: 'none',
                            backgroundClip: 'unset',
                            webkitBackgroundClip: 'unset',
                            webkitTextFillColor: '#333333',
                            // Original-Text-Eigenschaften
                            fontFamily: style.fontFamily,
                            fontSize: style.fontSize,
                            fontWeight: style.fontWeight,
                            lineHeight: style.lineHeight,
                            letterSpacing: style.letterSpacing,
                            textAlign: style.textAlign || 'inherit',
                            whiteSpace: style.whiteSpace || 'pre-wrap'
                        }}
                    />
                    {renderActionButtons()}
                </div>
            ) : (
                // Input für einfache Texte
                <input
                    type="text"
                    value={inlineValue}
                    onChange={(e) => setInlineValue(e.target.value)}
                    onKeyDown={(e) => handleInlineKeyPress(e, fieldName)}
                    onBlur={() => handleInlineSubmit(fieldName)}
                    placeholder={placeholder}
                    autoFocus
                    ref={(input) => {
                        if (input) {
                            setTimeout(() => {
                                if (input.selectionStart === 0 && input.selectionEnd === 0) {
                                    input.setSelectionRange(input.value.length, input.value.length);
                                }
                            }, 0);
                        }
                    }}
                    style={{
                        ...style,
                        width: layoutData.width || style.width,
                        height: layoutData.height || style.height,
                        minWidth: layoutData.width || style.minWidth,
                        maxWidth: layoutData.width || 'none',
                        minHeight: layoutData.height || 'auto',
                        maxHeight: layoutData.height || 'none',
                        marginTop: layoutData.marginTop || '0',
                        marginBottom: layoutData.marginBottom || '0',
                        marginLeft: layoutData.marginLeft || '0',
                        marginRight: layoutData.marginRight || '0',
                        backgroundColor: 'transparent',
                        border: 'none',
                        outline: 'none',
                        padding: style.padding,
                        display: style.display,
                        alignItems: style.alignItems,
                        justifyContent: style.justifyContent,
                        alignSelf: style.alignSelf,
                        boxShadow: 'none',
                        cursor: 'text',
                        textAlign: style.textAlign || 'inherit',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        // GRADIENT-FIX: Grauer Text während Bearbeitung
                        color: '#333333',
                        backgroundImage: 'none',
                        backgroundClip: 'unset',
                        webkitBackgroundClip: 'unset',
                        webkitTextFillColor: '#333333',
                        // Original-Text-Eigenschaften
                        fontFamily: style.fontFamily,
                        fontSize: style.fontSize,
                        fontWeight: style.fontWeight
                    }}
                />
            );
        }

        // Edit-Modus: Inline-Editing ermöglichen
        const handleElementClick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            
            const rect = e.target.getBoundingClientRect();
            const computedStyle = window.getComputedStyle(e.target);
            
            const layoutData = {
                width: rect.width + 'px',
                height: rect.height + 'px',
                marginTop: computedStyle.marginTop,
                marginBottom: computedStyle.marginBottom,
                marginLeft: computedStyle.marginLeft,
                marginRight: computedStyle.marginRight
            };
            
            setOriginalLayout(layoutData);
            handleInlineEdit(fieldName, currentValue);
        };

        return (
            <Element 
                style={{
                    ...style,
                    cursor: 'text',
                    position: 'relative',
                    transition: 'all 0.2s ease',
                    padding: '4px 8px',
                    borderRadius: '4px'
                }}
                onClick={handleElementClick}
                onMouseEnter={(e) => {
                    e.target.style.cursor = 'text';
                }}
                onMouseLeave={(e) => {
                    e.target.style.cursor = 'text';
                }}
                title="Click to edit"
            >
                {currentValue || placeholder}
            </Element>
        );
    };

    // Lightbox Functions - Memoized um Re-Rendering zu verhindern
    const openLightbox = React.useCallback((image) => {
        if (!isEditMode && data.enableLightbox) {
            setLightboxImage(image);
            setLightboxOpen(true);
        }
    }, [isEditMode, data.enableLightbox]);

    const closeLightbox = React.useCallback(() => {
        setLightboxOpen(false);
        setLightboxImage(null);
    }, []);

    // Widget Styling - EXAKT WIE HEROHEADER
    function isDark(color) {
        const rgb = parseInt(color.slice(1), 16);
        const r = (rgb >> 16) & 0xff;
        const g = (rgb >>  8) & 0xff;
        const b = (rgb >>  0) & 0xff;
        const luma = 0.299 * r + 0.587 * g + 0.114 * b;
        return luma < 128;
    }

    function getBorderStyle() {
        if (isEditMode) {
            if (isSelected) {
                const borderColor = isDark(data.backgroundColor) ? '#FFFFFF' : '#000000';
                return `2px dashed ${borderColor}`;
            } else {
                return '2px dashed transparent';
            }
        } else {
            return 'none';
        }
    }

    // Generate responsive widget styles using new layout system - WIE HEROHEADER
    const baseWidgetStyle = generateLayoutStyles(data, isMobileLayout, {
        backgroundColor: data.backgroundColor,
        border: getBorderStyle(),
        borderRadius: appendPx(data.borderRadius),
        position: 'relative',
        overflow: 'hidden',
        width: '100%'
    });

    const widgetStyle = {
        ...baseWidgetStyle,
        // ✅ EINHEITLICHE RESPONSIVE HEIGHT LOGIK - AUTO HEIGHT für Masonry
        ...(baseWidgetStyle.height === 'auto' && !baseWidgetStyle.minHeight && {
            minHeight: 'auto' // Gallery braucht keine feste Höhe
        }),
        paddingLeft: '20px',
        paddingRight: '20px'
    };

    // Text Alignment Configuration
    const getTextAlignment = () => {
        return isMobileLayout ? 
            (data.textAlignmentMobile || data.textAlignment || 'center') :
            (data.textAlignment || 'center');
    };

    const textAlignment = getTextAlignment();

    // Gallery Images - mit Fallback für leere Arrays und Memoization
    const galleryImages = React.useMemo(() => data.galleryImages || [], [data.galleryImages]);

    // Masonry Grid Styles - CSS Columns für echtes Masonry Layout
    const masonryStyles = React.useMemo(() => {
        const columns = isMobileLayout ? (data.columnCountMobile || 2) : (data.columnCount || 3);
        const gap = isMobileLayout ? (data.gridGapMobile || 12) : (data.gridGap || 16);
        
        return {
            columnCount: columns,
            columnGap: `${gap}px`,
            width: '100%'
        };
    }, [isMobileLayout, data.columnCount, data.columnCountMobile, data.gridGap, data.gridGapMobile]);

    // Individual Image Styles für Masonry
    const getImageItemStyles = (gap) => ({
        breakInside: 'avoid',
        marginBottom: `${gap}px`,
        display: 'inline-block',
        width: '100%'
    });

    return (
        <>
            {/* CSS Animations - Optimiert für Performance */}
            <style>
                {`
                    @keyframes blink {
                        0%, 50% { opacity: 1; }
                        51%, 100% { opacity: 0; }
                    }
                    
                    .gallery-widget .gallery-image {
                        width: 100%;
                        height: auto;
                        object-fit: cover;
                        border-radius: ${data.imageRadius !== undefined ? data.imageRadius : 8}px;
                        border: ${(() => {
                            const borderWidth = data.borderWidth !== undefined ? data.borderWidth : 0;
                            return data.border && borderWidth > 0 ? `${borderWidth}px solid ${data.borderColor || '#CCCCCC'}` : 'none';
                        })()};
                        transition: transform 0.3s ease, box-shadow 0.3s ease;
                        cursor: ${!isEditMode && data.enableLightbox ? 'pointer' : 'default'};
                        will-change: transform;
                        backface-visibility: hidden;
                        display: block;
                        vertical-align: top;
                    }
                    
                    .gallery-widget .gallery-image:hover {
                        transform: ${data.enableHover && !isEditMode ? 'scale(1.05)' : 'none'};
                        box-shadow: ${data.enableHover && !isEditMode ? '0 8px 25px rgba(0, 0, 0, 0.15)' : 'none'};
                    }
                    
                    .gallery-widget .lightbox-overlay {
                        position: fixed;
                        top: 0;
                        left: 0;
                        width: 100%;
                        height: 100%;
                        background: rgba(0, 0, 0, 0.9);
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        z-index: 10000;
                        cursor: pointer;
                        backdrop-filter: blur(4px);
                    }
                    
                    .gallery-widget .lightbox-image {
                        max-width: 90%;
                        max-height: 90%;
                        object-fit: contain;
                        border-radius: 8px;
                        box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
                    }
                    
                    .gallery-widget .lightbox-close {
                        position: absolute;
                        top: 20px;
                        right: 30px;
                        color: white;
                        font-size: 40px;
                        font-weight: bold;
                        cursor: pointer;
                        z-index: 10001;
                        text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
                        transition: opacity 0.2s ease;
                    }
                    
                    .gallery-widget .lightbox-close:hover {
                        opacity: 0.7;
                    }
                `}
            </style>
            
            <div 
                className={`gallery-widget`}
                style={widgetStyle} 
                ref={(el) => {
                    containerRef.current = el;
                    widgetRef.current = el;
                }}
            >
                {/* Delete Button */}
                {isEditMode && (
                    <button className="delete_button" onClick={() => setShowDeleteModal(true)}>
                        <img className="delete_logo" src={deleteLogo} alt="Löschen" />
                    </button>
                )}
                {isEditMode && showDeleteModal && (
                    <DeleteModal
                        isOpen={showDeleteModal}
                        onClose={() => setShowDeleteModal(false)}
                        onConfirm={(event) => handleDelete(event, data.id)}
                    />
                )}

                {/* AI Refresh Icon */}
                {isEditMode && (
                    <AIRefreshIcon 
                        widgetId={data.id}
                        widgetType={data.type}
                        style={{
                            position: 'absolute',
                            top: '10px',
                            right: '50px',
                            zIndex: 1000
                        }}
                    />
                )}
                
                {/* Widget Content */}
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: textAlignment === 'center' ? 'center' : 
                               textAlignment === 'right' ? 'flex-end' : 'flex-start',
                    textAlign: textAlignment,
                    width: '100%',
                    maxWidth: '1200px',
                    margin: '0 auto'
                }}>
                    {/* ✅ TITLE CONTAINER mit Max Width Control */}
                    {data.showTitleText && (
                        <div style={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: textAlignment === 'left' ? 'flex-start' : 
                                          textAlignment === 'right' ? 'flex-end' : 'center',
                            marginBottom: appendPx(data.titleDescriptionGap || 16)
                        }}>
                            <div style={{
                                maxWidth: appendPx(data.titleMaxWidth || 800),
                                width: '100%',
                                textAlign: textAlignment
                            }}>
                                {(!data.titleHighlightWords || data.titleHighlightWords.trim() === '') ? (
                                    // Normal Title ohne Highlights - Inline Editable
                                    renderEditableText(
                                        'titleText',
                                        data.titleText,
                                        'h2',
                                        {
                                            ...((!data.titleEffect || data.titleEffect === 'none') && {
                                                color: data.titleColor || '#000000'
                                            }),
                                            fontSize: appendPx(isMobileLayout ? data.titleSizeMobile : data.titleSize || 36),
                                            fontWeight: data.titleWeight || '700',
                                            fontFamily: data.titleFont || fonts.globalBigFont,
                                            marginBottom: '0', // ✅ Margin vom Container übernommen
                                            lineHeight: '1.2',
                                            ...getTextEffectStyles(data.titleEffect),
                                            // Responsive Font-Sizes
                                            '--mobile-size': appendPx(data.titleSizeMobile || data.titleSize || 36),
                                            '--normal-size': appendPx(data.titleSize || 36),
                                            '--big-size': appendPx(data.titleSizeBig || data.titleSize || 36),
                                            '--mobile-weight': data.titleWeightMobile || data.titleWeight || '700',
                                            '--normal-weight': data.titleWeight || '700',
                                            '--big-weight': data.titleWeightBig || data.titleWeight || '700'
                                        },
                                        'Enter gallery title...'
                                    )
                                ) : (
                                    // Title mit Highlights - Nur Anzeige (kein Inline-Editing)
                                    <h2 style={{
                                        ...((!data.titleEffect || data.titleEffect === 'none') && {
                                            color: data.titleColor || '#000000'
                                        }),
                                        fontSize: appendPx(isMobileLayout ? data.titleSizeMobile : data.titleSize || 36),
                                        fontWeight: data.titleWeight || '700',
                                        fontFamily: data.titleFont || fonts.globalBigFont,
                                        marginBottom: '0', // ✅ Margin vom Container übernommen
                                        lineHeight: '1.2',
                                        ...getTextEffectStyles(data.titleEffect),
                                        // Responsive Font-Sizes
                                        '--mobile-size': appendPx(data.titleSizeMobile || data.titleSize || 36),
                                        '--normal-size': appendPx(data.titleSize || 36),
                                        '--big-size': appendPx(data.titleSizeBig || data.titleSize || 36),
                                        '--mobile-weight': data.titleWeightMobile || data.titleWeight || '700',
                                        '--normal-weight': data.titleWeight || '700',
                                        '--big-weight': data.titleWeightBig || data.titleWeight || '700'
                                    }}>
                                        <HighlightText
                                            text={data.titleText}
                                    highlightWords={data.titleHighlightWords}
                                    baseStyle={{}}
                                    highlightFont={data.titleHighlightFont || fonts.globalSmallFont}
                                    highlightColor={data.titleHighlightColor}
                                    highlightWeight={data.titleHighlightWeight}
                                    highlightSize={data.titleHighlightSize || data.titleSize || 36}
                                    highlightSizeMobile={data.titleHighlightSizeMobile || data.titleSizeMobile || 28}
                                    highlightSizeBig={data.titleHighlightSizeBig || data.titleSizeBig || 40}
                                    highlightMarginLeft={data.titleHighlightMarginLeft || 0}
                                    highlightMarginRight={data.titleHighlightMarginRight || 0}
                                    highlightEffect={data.titleHighlightEffect}
                                    getTextEffectStyles={getTextEffectStyles}
                                />
                            </h2>
                        )}
                            </div>
                        </div>
                    )}

                    {/* Description - Rich Text Support - EXAKT WIE HEROHEADER */}
                    {data.showDescriptionText && (
                        <div style={{
                            maxWidth: appendPx(data.descriptionMaxWidth || 600),
                            marginBottom: appendPx(data.descriptionGalleryGap || 40),
                            alignSelf: textAlignment === 'center' ? 'center' : 
                                      textAlignment === 'right' ? 'flex-end' : 'flex-start'
                        }}>
                            <div
                                style={{
                                    ...((!data.descriptionEffect || data.descriptionEffect === 'none') && {
                                        color: data.descriptionColor || '#666666'
                                    }),
                                    fontSize: appendPx(isMobileLayout ? data.descriptionSizeMobile : data.descriptionSize || 18),
                                    fontWeight: data.descriptionWeight || '400',
                                    fontFamily: data.descriptionFont || fonts.globalSmallFont,
                                    lineHeight: '1.6',
                                    ...getTextEffectStyles(data.descriptionEffect),
                                    // Responsive Font-Sizes - EXAKT WIE HEROHEADER
                                    '--mobile-size': appendPx(data.descriptionSizeMobile || data.descriptionSize || 18),
                                    '--normal-size': appendPx(data.descriptionSize || 18),
                                    '--big-size': appendPx(data.descriptionSizeBig || data.descriptionSize || 18),
                                    '--mobile-weight': data.descriptionWeightMobile || data.descriptionWeight || '400',
                                    '--normal-weight': data.descriptionWeight || '400',
                                    '--big-weight': data.descriptionWeightBig || data.descriptionWeight || '400'
                                }}
                                className="rich-text-output" // ✅ CSS-Klasse für Font-Inheritance
                                dangerouslySetInnerHTML={{ 
                                    __html: (() => {
                                        const content = data.descriptionText || 'Enter description text with <strong>bold</strong> formatting...';
                                        // ✅ NUR WIRKLICH ESCAPED HTML unescapen (nicht normales HTML!)
                                        if (content.includes('&lt;') || content.includes('&gt;') || content.includes('&amp;')) {
                                            return content
                                                .replace(/&lt;/g, '<')
                                                .replace(/&gt;/g, '>')
                                                .replace(/&amp;/g, '&')
                                                .replace(/&quot;/g, '"')
                                                .replace(/&#39;/g, "'")
                                                .replace(/&nbsp;/g, ' ');
                                        }
                                        return content;
                                    })()
                                }}
                            />
                        </div>
                    )}

                    {/* Gallery Grid - Echtes Masonry Layout ohne White-Space */}
                    {galleryImages.length > 0 && (
                        <div style={masonryStyles}>
                            {galleryImages.map((image, index) => {
                                // Stabile Key-Generierung um Re-Rendering zu verhindern
                                const stableKey = image.id || `gallery-img-${index}-${image.url?.split('/').pop()}`;
                                const gap = isMobileLayout ? (data.gridGapMobile || 12) : (data.gridGap || 16);
                                
                                return (
                                    <div key={stableKey} style={getImageItemStyles(gap)}>
                                        <img
                                            src={image.url}
                                            alt={image.alt || `Gallery image ${index + 1}`}
                                            title={image.title}
                                            className="gallery-image"
                                            onClick={() => openLightbox(image)}
                                            onError={(e) => {
                                                // Fallback für kaputte Bilder - verwende ein echtes Bild
                                                if (e.target.src !== '/img/image_placeholder.webp') {
                                                    e.target.src = '/img/image_placeholder.webp';
                                                }
                                            }}
                                            loading="lazy" // ✅ Lazy Loading für Performance
                                            style={{
                                                // Verhindere Layout-Shift während des Ladens
                                                width: '100%',
                                                height: 'auto',
                                                display: 'block'
                                            }}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Empty State für Edit Mode */}
                    {isEditMode && galleryImages.length === 0 && (
                        <div style={{
                            padding: '60px 20px',
                            textAlign: 'center',
                            color: '#999',
                            border: '2px dashed #ddd',
                            borderRadius: '8px',
                            backgroundColor: '#f9f9f9'
                        }}>
                            <p style={{ margin: 0, fontSize: '16px' }}>
                                No gallery images yet. Add images in the edit panel to create your gallery.
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Lightbox - Performance-optimiert */}
            {lightboxOpen && lightboxImage && (
                <div className="lightbox-overlay" onClick={closeLightbox}>
                    <span className="lightbox-close" onClick={closeLightbox}>&times;</span>
                    <img
                        src={lightboxImage.url}
                        alt={lightboxImage.alt}
                        title={lightboxImage.title}
                        className="lightbox-image"
                        onClick={(e) => e.stopPropagation()}
                        onLoad={(e) => {
                            // Smooth fade-in für bessere UX
                            e.target.style.opacity = '1';
                        }}
                        style={{
                            opacity: '0',
                            transition: 'opacity 0.3s ease'
                        }}
                    />
                </div>
            )}
        </>
    );
}

export default GalleryWidget;
